import React from 'react';
import { GitBranch, AlertCircle } from 'lucide-react';

const FlowsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <GitBranch className="w-6 h-6" />
          Fluxos de Atendimento
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Crie e gerencie fluxos personalizados de atendimento e disparos em massa
        </p>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          {/* Em Desenvolvimento Banner */}
          <div className="bg-gradient-to-r from-purple-500 to-blue-600 p-8 text-white text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <GitBranch className="w-12 h-12" />
              <h2 className="text-3xl font-bold">Editor Visual de Fluxos</h2>
            </div>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Em breve você poderá criar e editar fluxos de atendimento e disparos em massa de forma visual e intuitiva
            </p>
          </div>

          {/* Recursos Planejados */}
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-1">
                      Funcionalidade em Desenvolvimento
                    </h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-500">
                      Este módulo está sendo desenvolvido e estará disponível em breve. Por enquanto, os fluxos são gerenciados via código.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Recursos Planejados
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Recurso 1 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🎨</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Editor Visual</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Interface drag-and-drop para criar fluxos de forma visual, sem necessidade de programação
                  </p>
                </div>

                {/* Recurso 2 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">📝</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Templates Personalizados</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure templates WhatsApp, botões interativos e variáveis dinâmicas
                  </p>
                </div>

                {/* Recurso 3 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🔀</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Lógica Condicional</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Defina condições, ramificações e ações baseadas em respostas dos clientes
                  </p>
                </div>

                {/* Recurso 4 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🎯</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Roteamento Inteligente</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure para qual departamento/agente direcionar baseado nas respostas
                  </p>
                </div>

                {/* Recurso 5 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">📊</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Analytics em Tempo Real</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Visualize métricas de desempenho, taxa de conversão e efetividade dos fluxos
                  </p>
                </div>

                {/* Recurso 6 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">💾</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Biblioteca de Fluxos</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Salve, reutilize e compartilhe fluxos prontos entre diferentes campanhas
                  </p>
                </div>
              </div>

              {/* Exemplo Visual */}
              <div className="mt-12 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-lg p-8">
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
                  Prévia do Editor Visual
                </h4>
                <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-inner">
                  <div className="flex flex-col items-center gap-4">
                    {/* Início */}
                    <div className="bg-green-500 text-white px-6 py-3 rounded-full font-semibold shadow-lg">
                      🚀 Início do Fluxo
                    </div>

                    <div className="w-0.5 h-8 bg-gray-300 dark:bg-gray-600"></div>

                    {/* Template */}
                    <div className="bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold shadow-lg w-64 text-center">
                      📨 Enviar Template
                    </div>

                    <div className="w-0.5 h-8 bg-gray-300 dark:bg-gray-600"></div>

                    {/* Aguardar Resposta */}
                    <div className="bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold shadow-lg w-64 text-center">
                      ⏳ Aguardar Resposta
                    </div>

                    <div className="w-0.5 h-8 bg-gray-300 dark:bg-gray-600"></div>

                    {/* Decisão */}
                    <div className="bg-yellow-500 text-white px-6 py-3 rounded-lg font-semibold shadow-lg w-64 text-center">
                      🔀 Resposta = "Sim"?
                    </div>

                    <div className="flex gap-8">
                      {/* Sim */}
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">✓ Sim</span>
                        <div className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm shadow-lg">
                          👤 Transferir Financeiro
                        </div>
                      </div>

                      {/* Não */}
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-sm font-semibold text-red-600 dark:text-red-400">✗ Não</span>
                        <div className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm shadow-lg">
                          🔒 Encerrar Atendimento
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
                  Interface visual intuitiva para criar fluxos complexos sem código
                </p>
              </div>

              {/* CTA */}
              <div className="mt-8 text-center">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Por enquanto, os fluxos estão disponíveis em <strong>Disparos em Massa</strong>
                </p>
                <button
                  onClick={() => window.location.href = '/bulk-dispatches'}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Ir para Disparos em Massa
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowsPage;
